import { useState } from "react";
import { SharedIcon } from "../shared/shared-icon";
import { ColourPickerModal } from "./colour-picker-modal";

/**
 * "Colours used": the design's two entry pills, and the grid of chosen swatch cards beneath them.
 *
 * The field owns the chosen list and nothing else. It does not validate — `coloursUsed` is
 * optional, and the server checks every key it is given is a published, untrashed colourChip on
 * save, so a second copy of that rule here could only ever disagree with it. It does not fetch
 * either; the picker does, from its own modal.
 *
 * A colour is `{ key, name, code, rgb }`:
 *
 *   key   the chip's content GUID. THIS is what the form posts back as colourChipKeys.
 *   rgb   already wrapped as rgb(r, g, b), so the swatch is painted straight from it.
 *
 * The same shape reaches the page as `values.colours` (resolved server-side by
 * MemberProfileZoneController) and comes back from the picker's API, so the field never has to
 * know where a colour came from.
 *
 * Copy arrives as a prop, from the dictionary-backed object ZoneEditor.cshtml builds. Nothing
 * user-facing is typed in this file.
 *
 * `.button` and not `.cta`: `.cta` has no standalone rule anywhere in the SCSS tree — every
 * occurrence is a nested descendant selector inside another component — so a control carrying it
 * renders as a bare native button.
 */
export function ColourField({ colours = [], onChange, error, settings, copy = {} }) {
  // Which pill opened the modal, or null when it is closed. One modal, two sources: they answer in
  // the same shape and are painted with the same grid.
  const [source, setSource] = useState(null);

  const chosenKeys = colours.map((colour) => colour.key);

  const add = (added) => {
    // Keyed by the chip GUID, so a colour reached from the search and again from saved colours
    // lands once. Order is preserved: what the member chose first stays first.
    const merged = [...colours];

    added.forEach((colour) => {
      if (!merged.some((chosen) => chosen.key === colour.key)) {
        merged.push(colour);
      }
    });

    onChange(merged);
    setSource(null);
  };

  const remove = (key) => onChange(colours.filter((colour) => colour.key !== key));

  return (
    <div className="member-profile-colours" data-testid="project-colours">
      <div className="member-profile-colours__actions">
        <button
          type="button"
          className="button member-profile-colours__add"
          data-testid="project-colours-add"
          onClick={() => setSource("search")}
        >
          <SharedIcon iconName="#sprite-plus" className="icon left" aria-hidden="true" />
          {copy.addColour}
        </button>

        <button
          type="button"
          className="button member-profile-colours__add"
          data-testid="project-colours-add-saved"
          onClick={() => setSource("saved")}
        >
          <SharedIcon iconName="#sprite-plus" className="icon left" aria-hidden="true" />
          {copy.addSavedColours}
        </button>
      </div>

      {error && (
        <p className="member-profile-editor__error" role="alert" data-testid="project-colours-error">
          {error}
        </p>
      )}

      {colours.length > 0 && (
        <ul className="member-profile-colours__grid" data-testid="project-colours-list">
          {colours.map((colour) => (
            <li key={colour.key} className="member-profile-colours__card" data-testid="project-colour">
              <div
                className="member-profile-colours__swatch"
                style={{ backgroundColor: colour.rgb }}
              />

              <p className="member-profile-colours__name">{colour.name}</p>
              <p className="member-profile-colours__code">{colour.code}</p>

              {/* The bin the design puts under each swatch. This grid IS the member's selection,
                  so the only action it needs is taking one back out. */}
              <button
                type="button"
                className="member-profile-colours__remove"
                data-testid="project-colour-remove"
                onClick={() => remove(colour.key)}
              >
                <SharedIcon iconName="#sprite-bin" className="icon" aria-hidden="true" />

                {/* Named for a screen reader, because the visible control is an icon and there
                    would otherwise be one identically-labelled Remove button per swatch. */}
                <span className="member-profile-colours__sr">
                  {(copy.removeColour ?? "").replace("{0}", `${colour.name}, ${colour.code}`)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Keyed on the source so switching pills remounts rather than reusing the other source's
          state — the same reason the saved-colours modal is keyed on a fresh id. */}
      {source && (
        <ColourPickerModal
          key={source}
          isOpen={true}
          source={source}
          chosenKeys={chosenKeys}
          settings={settings}
          copy={copy}
          onClose={() => setSource(null)}
          onConfirm={add}
        />
      )}
    </div>
  );
}
